import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtRelative } from "@/lib/format";

/**
 * RatingWidget — 1-5 star design rating with optional comment.
 * - kind="internal" (any member) or "client" (manager/admin/owner only — backend enforces).
 * - Shows current avg + count, lets the current user submit/update their rating.
 */
export default function RatingWidget({ itemId, role = "designer" }) {
  const canRateAsClient = ["owner", "admin", "manager"].includes(role);
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [kind, setKind] = useState("internal");
  const [list, setList] = useState([]);
  const [avg, setAvg] = useState(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/review-items/${itemId}/ratings`);
      setList(data.ratings || []);
      setAvg(data.avg_stars);
      setCount(data.count || 0);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (itemId) load(); }, [itemId]);

  const submit = async () => {
    if (!stars) return toast.error("Pick a rating first");
    setBusy(true);
    try {
      await api.post(`/review-items/${itemId}/ratings`, { stars, kind, comment: comment || null });
      toast.success("Rating saved");
      setOpen(false);
      setStars(0); setComment("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't save rating"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-8" data-testid="rating-widget-trigger">
          <Star className="w-3.5 h-3.5 mr-1.5 text-amber-400 fill-current" />
          {avg ? `${avg} (${count})` : `Rate`}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="rating-widget-dialog">
        <DialogHeader>
          <DialogTitle>Rate this design</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-1.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                onMouseEnter={() => setHover(s)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setStars(s)}
                className="transition-transform hover:scale-110"
                data-testid={`rating-star-${s}`}
                aria-label={`${s} star`}
              >
                <Star className={`w-9 h-9 ${(hover || stars) >= s ? "text-amber-400 fill-current" : "text-muted-foreground"}`} />
              </button>
            ))}
          </div>
          <div>
            <div className="label-eyebrow mb-1">Rating type</div>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-8 text-xs" data-testid="rating-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">Internal review</SelectItem>
                {canRateAsClient && <SelectItem value="client">On behalf of client</SelectItem>}
              </SelectContent>
            </Select>
            {!canRateAsClient && (
              <p className="text-[10px] text-muted-foreground mt-1">Client ratings can only be entered by managers and above.</p>
            )}
          </div>
          <div>
            <div className="label-eyebrow mb-1">Comment (optional)</div>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What worked, what could improve?" data-testid="rating-comment" />
          </div>
          {list.length > 0 && (
            <div className="border-t border-border pt-3">
              <div className="label-eyebrow mb-2">All ratings · {count} · avg {avg}</div>
              <ul className="space-y-1.5 max-h-40 overflow-auto">
                {list.map((r) => (
                  <li key={r.id} className="text-xs border-l-2 border-primary/30 pl-2" data-testid={`rating-row-${r.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.rater_name}</span>
                      <span className="chip text-[9px]">{r.kind}</span>
                      <span className="inline-flex items-center">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-2.5 h-2.5 ${s <= r.stars ? "text-amber-400 fill-current" : "text-muted-foreground"}`} />
                        ))}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{fmtRelative(r.created_at)}</span>
                    </div>
                    {r.comment && <div className="text-muted-foreground mt-0.5 whitespace-pre-wrap">{r.comment}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="rating-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !stars} data-testid="rating-submit">
            {busy ? "Saving…" : "Save rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
